import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { stockService } from '../../services/stockService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import InvestmentQuestionnaire from '../../components/Investment/InvestmentQuestionnaire';
import InvestmentCreation from '../../components/Investment/InvestmentCreation';
import InvestmentLevelModal from '../../components/Investment/InvestmentLevelModal';
import ExistingInvestmentModal from '../../components/Investment/ExistingInvestmentModal';
import StockCard from '../Stocks/components/StockCard/StockCard';
import StockSearch from '../Stocks/components/StockSearch/StockSearch';
import './InvestmentStocks.css';

/* ── Small inline logo loader — used for page-level loading states ── */
const MiniLogoLoader = ({ label }) => (
  <div className="mini-loader-wrap">
    <div className="mini-logo">
      <div className="mini-bar-wrap mini-bar-left">
        <div className="mini-bar-fill mini-fill-left" />
      </div>
      <div className="mini-bar-wrap mini-bar-center">
        <div className="mini-bar-fill mini-fill-center" />
      </div>
      <div className="mini-bar-wrap mini-bar-right">
        <div className="mini-bar-fill mini-fill-right" />
      </div>
    </div>
    {label && <p className="mini-loader-label">{label}</p>}
  </div>
);

const InvestmentStocks = () => {
  const navigate = useNavigate();
  const { formatCurrency, currency, getCurrencySymbol } = useCurrency();
  
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [exchangeRate, setExchangeRate] = useState(null);
  
  const [selectedStock, setSelectedStock] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [showInvestmentCreation, setShowInvestmentCreation] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [showExistingInvestmentModal, setShowExistingInvestmentModal] = useState(false);
  const [existingInvestmentData, setExistingInvestmentData] = useState(null);

  const REQUIRED_LEVEL = 2;
  const REQUIRED_BALANCE_NGN = 300000; // ₦300,000

  const categories = [
    { id: 'all', label: 'All Assets', color: '#3b82f6' },
    { id: 'private-equity', label: 'Private Equity', color: '#dc2626' },
    { id: 'pre-ipo', label: 'Scale-Up', color: '#f59e0b' },
    { id: 'fpo', label: 'Growth Rounds', color: '#8b5cf6' },
    { id: 'listed', label: 'Listed', color: '#10b981' }
  ];

  useEffect(() => {
    fetchStocksAndWallet();
    fetchExchangeRate();
  }, []);

  useEffect(() => {
    filterStocksByCategory();
  }, [stocks, activeCategory, searchTerm]);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const convertNGNtoUSD = (amountNGN) => {
    if (!exchangeRate || !exchangeRate.ngn_to_usd) {
      // Fallback rate if API fails
      return amountNGN / 1600;
    }
    return amountNGN * parseFloat(exchangeRate.ngn_to_usd);
  };

  const getRequiredBalance = () => {
    if (currency === 'USD') {
      const usdAmount = convertNGNtoUSD(REQUIRED_BALANCE_NGN);
      return `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₦${REQUIRED_BALANCE_NGN.toLocaleString('en-NG')}`;
  };

  const fetchStocksAndWallet = async () => {
    try {
      setLoading(true);
      
      const stocksData = await stockService.getAllStocks();
      
      const walletResponse = await fetch(`${API_BASE_URL}/users/wallet/balance/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        console.log('💰 InvestmentStocks - Wallet Balance:', walletData);
        
        setWalletBalance({
          balance: walletData.balance,
          available_balance: walletData.available_balance,
          user_level: walletData.level,
          currency: walletData.currency
        });
      } else {
        console.error('Failed to fetch wallet balance');
        setWalletBalance({ balance: 0, available_balance: 0, user_level: 1 });
      }
      
      setStocks(stocksData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const filterStocksByCategory = () => {
    let filtered = stocks;

    if (activeCategory !== 'all') {
      filtered = stocks.filter(stock => {
        switch (activeCategory) {
          case 'private-equity':
            return stock.listing_stage === 'PRIVATE_EQUITY';
          case 'pre-ipo':
            return stock.listing_stage === 'PRE_IPO';
          case 'fpo':
            return stock.listing_stage === 'FPO';
          case 'listed':
            return stock.listing_stage === 'LISTED';
          default:
            return true;
        }
      });
    }

    if (searchTerm.trim()) {
      filtered = filtered.filter(stock =>
        stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stock.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredStocks(filtered);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleStockSelect = (stock) => {
    if (walletBalance && walletBalance.user_level < REQUIRED_LEVEL) {
      setShowLevelModal(true);
      return;
    }
    
    setSelectedStock(stock);
    setShowQuestionnaire(true);
  };

  const getCategoryDescription = () => {
    switch (activeCategory) {
      case 'private-equity':
        return 'Private Equity assets - Early-stage companies with high growth potential';
      case 'pre-ipo':
        return 'Scale-Up assets - Companies preparing for public listing';
      case 'fpo':
        return 'GRA assets - Growth Rounds Assets opportunities';
      case 'listed':
        return 'Listed assets - Publicly traded companies on the exchange';
      default:
        return 'Browse all available investment opportunities across all listing stages';
    }
  };

  const getStockBadgeColor = (listingStage) => {
    switch (listingStage) {
      case 'PRIVATE_EQUITY': return '#dc2626';
      case 'PRE_IPO': return '#f59e0b';
      case 'FPO': return '#8b5cf6';
      case 'LISTED': return '#10b981';
      default: return '#666';
    }
  };

  const getStockBadgeText = (listingStage) => {
    switch (listingStage) {
      case 'PRIVATE_EQUITY': return 'Private Equity';
      case 'PRE_IPO': return 'Scale-Up';
      case 'FPO': return 'GRA';
      case 'LISTED': return 'Listed';
      default: return 'Unknown';
    }
  };

  const handleQuestionnaireError = (errorData) => {
    console.log('🚫 Questionnaire Error:', errorData);
    
    if (errorData.existing_investment) {
      setExistingInvestmentData({
        investment: errorData.existing_investment,
        stockSymbol: selectedStock.symbol
      });
      setShowExistingInvestmentModal(true);
      setShowQuestionnaire(false);
    }
  };

  const handleNavigateToInvestment = (investmentId) => {
    navigate('/investment/plans', { state: { highlightId: investmentId } });
  };

  if (loading) {
    return (
      <div className="investment-stocks-container">
        <div className="loading">
          <MiniLogoLoader label="Loading investment opportunities..." />
        </div>
      </div>
    );
  }

  if (error && !showQuestionnaire && !showInvestmentCreation) {
    return (
      <div className="investment-stocks-container">
        <div className="error-message">{error}</div>
        <button className="back-button" onClick={() => navigate('/stocks')}>
          ← Back to Stocks
        </button>
      </div>
    );
  }

  return (
    <div className="investment-stocks-container">
      <div className="investment-header">
        <button className="back-button" onClick={() => navigate('/stocks')}>
          ← Back to Categories
        </button>
        <div className="header-content">
          <h1>Investment Opportunities</h1>
          <p>Select an asset to start your investment journey</p>
          {walletBalance && (
            <div className="level-info">
              <span className="level-badge">Level {walletBalance.user_level}</span>
              <span className="balance">Available: {formatCurrency(walletBalance.balance)}</span>
              {walletBalance.user_level < REQUIRED_LEVEL && (
                <span className="level-warning">
                  Upgrade to Level {REQUIRED_LEVEL} ({getRequiredBalance()}+) to access investments
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="category-filters">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-filter ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
            style={{ 
              '--category-color': category.color,
              backgroundColor: activeCategory === category.id ? category.color : 'white',
              color: activeCategory === category.id ? 'white' : category.color,
              borderColor: category.color
            }}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="category-description">
        <p>{getCategoryDescription()}</p>
      </div>

      <div className="investment-search">
        <StockSearch onSearch={handleSearch} placeholder="Search investment opportunities..." />
      </div>

      <div className="investment-stocks-grid">
        <h2 className="grid-title">
          {activeCategory === 'all' ? 'All Investment Opportunities' : categories.find(c => c.id === activeCategory)?.label}
          <span className="stocks-count">({filteredStocks.length} assets)</span>
        </h2>
        
        {filteredStocks.length === 0 ? (
          <div className="no-stocks">
            <p>No investment opportunities found {searchTerm && `for "${searchTerm}"`}</p>
          </div>
        ) : (
          <div className="stocks-list">
            {filteredStocks.map(stock => (
              <div key={stock.id} className="investment-stock-card-wrapper">
                <StockCard stock={stock} customAction={() => handleStockSelect(stock)} />
                <div className="investment-badge" style={{ backgroundColor: getStockBadgeColor(stock.listing_stage) }}>
                  {getStockBadgeText(stock.listing_stage)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InvestmentLevelModal
        isOpen={showLevelModal}
        onClose={() => setShowLevelModal(false)}
        currentLevel={walletBalance?.user_level || 1}
        currentBalance={walletBalance?.balance || 0}
        requiredLevel={REQUIRED_LEVEL}
        requiredBalance={REQUIRED_BALANCE_NGN}
        formatCurrency={formatCurrency}
      />

      {showExistingInvestmentModal && existingInvestmentData && (
        <ExistingInvestmentModal
          isOpen={showExistingInvestmentModal}
          onClose={() => {
            setShowExistingInvestmentModal(false);
            setExistingInvestmentData(null);
            setSelectedStock(null);
          }}
          existingInvestment={existingInvestmentData.investment}
          stockSymbol={existingInvestmentData.stockSymbol}
          onNavigateToInvestment={handleNavigateToInvestment}
        />
      )}

      {showQuestionnaire && selectedStock && (
        <InvestmentQuestionnaire
          stock={selectedStock}
          onComplete={(questionnaire) => {
            setQuestionnaireData(questionnaire);
            setShowQuestionnaire(false);
            setShowInvestmentCreation(true);
          }}
          onClose={() => {
            setShowQuestionnaire(false);
            setSelectedStock(null);
          }}
          onError={handleQuestionnaireError}
        />
      )}

      {showInvestmentCreation && selectedStock && questionnaireData && (
        <InvestmentCreation
          stock={selectedStock}
          questionnaire={questionnaireData}
          onSuccess={(investment) => {
            setShowInvestmentCreation(false);
            setSelectedStock(null);
            setQuestionnaireData(null);
            alert(`Investment created successfully for ${selectedStock.symbol}!`);
            fetchStocksAndWallet();
          }}
          onClose={() => {
            setShowInvestmentCreation(false);
            setSelectedStock(null);
            setQuestionnaireData(null);
          }}
        />
      )}
    </div>
  );
};

export default InvestmentStocks;